//@ts-nocheck
import React from 'react';
import { Button, ButtonProps } from 'antd';
import styled from 'styled-components';
import { withTruncatedButtonLabel } from './ScopeButtonLabel';

const buttonLabelTruncationStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  max-width: 100%;

  .ant-btn-icon,
  > .anticon {
    flex-shrink: 0;
  }

  .scope-button-label,
  > span:not(.ant-btn-icon):not(.anticon):not(.testpic):not(.show-on-hover) {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export interface ScopeButtonProps extends ButtonProps {
  width?: string;
  height?: string;
  fontSize?: string;
  lineheight?: string;
  bg_color?: string;
  border_color?: string;
  type?: string;
  fontWeight?: string;
  /** Preserved visual variant when type is remapped for antd Button. */
  scopeVariant?: string;
}

const ANT_BUTTON_TYPES = new Set([
  'default',
  'primary',
  'dashed',
  'link',
  'text',
]);

const resolveScopeVariant = (props: ScopeButtonProps) =>
  props.scopeVariant ?? props.type;

const StyledScopeButton = styled(Button)
  .withConfig({
    shouldForwardProp: (prop) =>
      ![
        'width',
        'height',
        'fontSize',
        'lineheight',
        'bg_color',
        'border_color',
        'fontWeight',
        'scopeVariant',
      ].includes(prop),
  })
  .attrs<ScopeButtonProps>((props) => {
    const scopeVariant = props.scopeVariant ?? props.type;
    return {
      type: ANT_BUTTON_TYPES.has(String(scopeVariant ?? ''))
        ? scopeVariant
        : 'default',
      scopeVariant,
    };
  })<ScopeButtonProps>`
  ${buttonLabelTruncationStyles}

  width: ${(props) => (props.width ? props.width : '330px')};
  height: ${(props) => (props.height ? props.height : '45px')};
  font-size: ${(props) => (props.fontSize ? props.fontSize : '15px')};
  font-weight: 600;
  line-height: ${(props) => (props.lineheight ? props.lineheight : '24px')};
  border-radius: 50px;
  box-shadow: none;

  background-color: ${(props) => {
    const variant = resolveScopeVariant(props);
    return props.bg_color
      ? props.bg_color
      : variant === 'dark-orange'
      ? 'var(--btn-accent-bg, #FFBC3E)'
      : variant === 'warning-red'
      ? 'var(--btn-warning-bg, #FCE7E7)'
      : variant === 'primary' || variant === 'dark'
      ? 'var(--btn-primary-color, #41aaaf)'
      : 'none';
  }} !important;

  color: ${(props) => {
    const variant = resolveScopeVariant(props);
    return variant === 'default'
      ? 'var(--btn-primary-color, #41aaaf)'
      : variant === 'dark-orange'
      ? 'var(--btn-dark-text, #042A2A)'
      : variant === 'warning-red'
      ? 'var(--btn-warning-text, #D13131)'
      : 'var(--color-on-primary, #ffffff)';
  }} !important;

  border-color: ${(props) => {
    const variant = resolveScopeVariant(props);
    return variant == 'default'
      ? 'var(--btn-primary-color, #41aaaf)'
      : variant === 'dark-orange'
      ? 'var(--btn-accent-bg, #FFBC3E)'
      : variant === 'warning-red'
      ? 'var(--btn-warning-bg, #FCE7E7)'
      : 'var(--btn-primary-color, #41aaaf)';
  }} !important;

  ${(props) => props.lineheight == '12px' && `padding: 4px 12px;`}

  /* :focus-visible only. A mouse click leaves :focus after a drawer closes, which kept the hover fill. */
  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus-visible,
  &:not(:disabled):not(.ant-btn-disabled):active {
    box-shadow: none;

    .testpic {
      color: var(--btn-dark-text, #042a2a);
      background: var(--btn-icon-hover-bg, #fff5e2);
    }

    background-color: ${(props) => {
      const variant = resolveScopeVariant(props);
      return variant == 'default'
        ? 'var(--btn-primary-color, #41aaaf)'
        : variant === 'dark-orange'
        ? 'var(--btn-accent-hover, #EDAC32)'
        : variant === 'warning-red'
        ? 'var(--btn-warning-text, #D13131)'
        : variant === 'primary' || variant === 'dark'
        ? 'var(--btn-primary-hover-bg, #64bbbd)'
        : 'var(--btn-accent-bg, #ffbc3e)';
    }} !important;

    color: ${(props) => {
      const variant = resolveScopeVariant(props);
      return variant == 'default'
        ? '#FFFFFF'
        : variant === 'warning-red'
        ? 'var(--btn-warning-bg, #FCE7E7)'
        : variant === 'primary' || variant === 'dark'
        ? 'var(--color-on-primary, #ffffff)'
        : 'var(--btn-dark-text, #042a2a)';
    }} !important;

    border-color: ${(props) => {
      const variant = resolveScopeVariant(props);
      return variant === 'warning-red'
        ? 'var(--btn-warning-bg, #FCE7E7)'
        : variant === 'dark-orange'
        ? 'var(--btn-accent-hover, #EDAC32)'
        : 'var(--btn-primary-hover-bg, #64bbbd)';
    }} !important;
  }

  ${(props) =>
    resolveScopeVariant(props) === 'warning-red' &&
    !props.disabled &&
    `
    &&.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled),
    &&.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled) {
      background-color: var(--btn-warning-bg, #fce7e7) !important;
      color: var(--btn-warning-text, #d13131) !important;
      border-color: var(--btn-warning-bg, #fce7e7) !important;

      .anticon {
        color: var(--btn-warning-text, #d13131) !important;
      }
    }

    &&.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled):hover,
    &&.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled):focus-visible,
    &&.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled):hover,
    &&.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled):focus-visible {
      background-color: var(--btn-warning-text, #d13131) !important;
      color: var(--btn-warning-bg, #fce7e7) !important;
      border-color: var(--btn-warning-bg, #fce7e7) !important;
      box-shadow: none;

      .anticon {
        color: var(--btn-warning-bg, #fce7e7) !important;
      }
    }
  `}

  .testpic {
    color: #ffffff;
    font-size: 16px;
    width: 28px;
    height: 28px;
    background: var(--btn-icon-bg, #48b9bf);
    border-radius: 100px;
    display: flex;
    justify-content: center;
    margin-right: 10px;
    margin-left: -30px;
  }

  ${(props) =>
    props.disabled &&
    `
    cursor: not-allowed !important;
    border-color: var(--btn-disabled-border, #cccccc) !important;
    color: var(--btn-disabled-text, rgba(46, 46, 46, 0.25)) !important;
    background-color: var(--btn-disabled-bg, rgba(46, 46, 46, 0.04)) !important;
    box-shadow: none;
    
    &:hover {
      background-color: var(--btn-disabled-bg, rgba(46, 46, 46, 0.04)) !important;
      color: var(--btn-disabled-text, rgba(46, 46, 46, 0.25)) !important;
      border-color: var(--btn-disabled-border, #cccccc) !important;
      box-shadow: none;
    }
  `}
`;

export const ScopeButton = withTruncatedButtonLabel(
  StyledScopeButton,
) as React.ForwardRefExoticComponent<
  ScopeButtonProps & React.RefAttributes<HTMLElement>
>;

interface SSOLoginButtonProps extends ButtonProps {
  minwidth?: string;
  marginright?: string;
}
const StyledSSOLoginButton = styled(Button)
  .withConfig({
    shouldForwardProp: (prop) => !['minwidth', 'marginright'].includes(prop),
  })
  .attrs((props) => ({
    className: ['scope-sso-login-button', props.className]
      .filter(Boolean)
      .join(' '),
  }))<SSOLoginButtonProps>`
  ${buttonLabelTruncationStyles}

  &&.ant-btn {
    position: relative;
    overflow: visible;
    min-width: ${(props) => (props.minwidth ? props.minwidth : '320px')};
    height: 48px;
    border-radius: 50px;
    background: var(--color-sso-button-bg, #d2edef) !important;
    background-color: var(--color-sso-button-bg, #d2edef) !important;
    border-color: var(--color-sso-button-bg, #d2edef) !important;
    color: var(--color-text-dark, #042a2a) !important;
    font-family: Inter, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
    box-shadow: none !important;
    /* Absolute provider logo on the left; equal padding keeps the label centred */
    justify-content: center;
    padding-left: 48px;
    padding-right: 48px;
    margin-right: ${(props) => (props.marginright ? props.marginright : '0')};
    text-align: center;

    .scope-sso-login-label,
    .scope-button-label {
      flex: 0 1 auto;
      text-align: center;
    }

    > img.scope-sso-qbo-hover {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      margin: 0;
      max-height: 36px;
      max-width: calc(100% - 72px);
      width: auto;
      opacity: 0;
      pointer-events: none;
    }
  }

  &&.ant-btn:hover,
  &&.ant-btn:focus-visible,
  &&.ant-btn:active {
    box-shadow: none !important;
    background: var(
      --color-sso-button-hover,
      var(--color-primary-hover, #64bbbd)
    ) !important;
    background-color: var(
      --color-sso-button-hover,
      var(--color-primary-hover, #64bbbd)
    ) !important;
    border-color: var(
      --color-sso-button-hover,
      var(--color-primary-hover, #64bbbd)
    ) !important;
    color: var(--color-text-dark, #042a2a) !important;
  }

  /* Intuit: keep rest pill colour and overlay official artwork (do not teal-fill). */
  &&.ant-btn.scope-sso-login-button--qbo:hover,
  &&.ant-btn.scope-sso-login-button--qbo:focus-visible,
  &&.ant-btn.scope-sso-login-button--qbo:active {
    background: var(--color-sso-button-bg, #d2edef) !important;
    background-color: var(--color-sso-button-bg, #d2edef) !important;
    border-color: var(--color-sso-button-bg, #d2edef) !important;
  }

  &&.ant-btn.scope-sso-login-button--qbo:hover .scope-sso-qbo-label,
  &&.ant-btn.scope-sso-login-button--qbo:focus-visible .scope-sso-qbo-label {
    visibility: hidden;
  }

  &&.ant-btn.scope-sso-login-button--qbo:hover > img.scope-sso-qbo-hover,
  &&.ant-btn.scope-sso-login-button--qbo:focus-visible
    > img.scope-sso-qbo-hover {
    opacity: 1;
  }
`;

export const SSOLoginButton = withTruncatedButtonLabel(StyledSSOLoginButton);

export const LinkButton = styled(Button).withConfig({
  shouldForwardProp: (prop) => prop !== 'color',
})`
  &&.ant-btn-link {
    color: ${(props) => (props.color ? props.color : '#5a6a6a')} !important;
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 600;
    line-height: 24px;
    text-align: left;
    padding: 0;
    height: auto;
    align-self: flex-start;
    flex-shrink: 0;

    > span {
      text-decoration: underline;
      text-underline-offset: 3px;
    }

    &:hover,
    &:focus,
    &:active {
      color: ${(props) => (props.color ? props.color : '#5a6a6a')} !important;
      background: transparent !important;
    }
  }
`;

export const IconContextButton = styled(Button)`
    color: var(--color-text-base, #2e2e2e);
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    padding: 4px 2px !important;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    text-align: start;
    text-decoration: none !important;

  .ant-btn-icon {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    width: 18px;
    min-width: 18px;
    height: 22px;
    margin-inline-end: 0 !important;
    flex-shrink: 0;
  }

  .ant-btn-icon .anticon {
    font-size: 16px;
    line-height: 1;
  }

  .ant-btn-icon svg,
  .ant-btn-icon .anticon svg {
    display: block;
    width: 18px;
    height: 18px;
  }

  > span:not(.ant-btn-icon),
  > a {
    min-width: 0;
    text-align: start;
  }

  .anticon,
  .anticon svg {
    color: currentColor !important;
  }

  .anticon svg path[fill]:not([fill='none']) {
    fill: currentColor !important;
  }

  &.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled),
  &.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled) {
    color: var(--color-error, #b94a48) !important;

    .anticon {
      color: var(--color-error, #b94a48) !important;
    }
  }
    
  ${(props) =>
    !props.disabled &&
    `
    &:hover {
      color: var(--color-text-dark, #042a2a);
      background-color: var(--btn-icon-hover-bg, #fff5e2) !important;
    }

    &.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled):hover,
    &.ant-btn-dangerous:not(:disabled):not(.ant-btn-disabled):focus,
    &.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled):hover,
    &.ant-btn-color-dangerous:not(:disabled):not(.ant-btn-disabled):focus {
      color: var(--color-error, #b94a48) !important;
      background-color: var(--btn-icon-hover-bg, #fff5e2) !important;

      .anticon {
        color: var(--color-error, #b94a48) !important;
      }
    }
    `}
    
  ${(props) =>
    props.disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed !important;
    `}
}`;

export const ContextButton = styled(Button)`
  border: 1px solid transparent;
  width: 33px;
  height: 25px;
  padding: 2px 2px;
  background-color: ${(props) => (props.bgcolor ? props.bgcolor : 'none')};
  border-radius: 5px;
  color: var(--color-text-base, #2e2e2e);

  .anticon {
    color: currentColor !important;
  }

    &:hover,
    &:active {
      border: 1px solid var(--color-primary, #41AAAF);
      background-color: var(--color-surface-raised, #FFFFFF) !important;
      color: var(--color-text-base, #2e2e2e);
    }
}`;

export const IconButton = styled(Button)`
    border: 2px solid var(--color-border, #DBEAE3);
    box-shadow: 0px 0px 10px 0px #0000000D;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    text-align: left;
    color: ${(props) =>
      props.color ? props.color : 'var(--color-text-base, #2e2e2e)'};
    border-radius: 10px;

    .anticon {
      color: currentColor !important;
    }

    &:hover {
      color: ${(props) =>
        props.disabled ? '' : 'var(--color-text-base, #2e2e2e)'} !important;
      border: ${(props) =>
        props.disabled
          ? ''
          : '2px solid var(--color-primary, #41AAAF)'} !important;
    }
}`;

export const ToggleButton = styled(Button).attrs((props) => ({
  className: ['scope-toggle-button', 'scope-icon-chip', props.className]
    .filter(Boolean)
    .join(' '),
  rootClassName: ['scope-toggle-button', 'scope-icon-chip', props.rootClassName]
    .filter(Boolean)
    .join(' '),
}))<{ paddinglft?: string }>`
  .show-on-hover {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.3s ease-in-out, visibility 0s 0.3s;
    white-space: nowrap;
    margin-left: 0;
  }
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0;
  background-color: var(--color-toggle-chip-bg, #e9f6f7) !important;
  width: 25px;
  min-width: 25px !important;
  padding-inline-start: 4px !important;
  height: 26px;
  color: var(--color-toggle-chip-text, #042a2a) !important;
  border: 1px solid var(--color-toggle-chip-border, #dbeae3) !important;
  margin-inline-start: -4px;
  box-shadow: none !important;

  &.ant-btn-primary:not(:hover) {
    background-color: var(--color-toggle-chip-bg, #e9f6f7) !important;
    color: var(--color-toggle-chip-text, #042a2a) !important;
    border-color: var(--color-toggle-chip-border, #dbeae3) !important;
  }

  &:not(:hover) .anticon {
    color: var(--color-toggle-chip-text, #042a2a) !important;
  }

  transition: padding-inline-end 0.3s ease-in-out, min-width 0.3s ease-in-out,
    background-color 0.3s ease-in-out, border-radius 0.3s ease-in-out,
    padding-left 0.3s ease-in-out, font-size 0.3s ease-in-out,
    padding-top 0.3s ease-in-out;
  padding-left: 5px;
  border-radius: 50% !important;
  overflow: hidden;
  &:hover {
    padding-inline-end: ${(props) =>
      props.paddinglft ? props.paddinglft : '82px'} !important;
    min-width: ${(props) =>
      props.paddinglft ? props.paddinglft : '82px'} !important;
    border-radius: 50px !important;
    padding-left: 4px;
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    padding-top: 2px;
    background-color: var(
      --color-toggle-chip-hover-bg,
      var(--color-primary, #41aaaf)
    ) !important;
    color: var(--color-toggle-chip-hover-text, #ffffff) !important;
    border-color: var(
      --color-toggle-chip-hover-bg,
      var(--color-primary, #41aaaf)
    ) !important;

    .anticon {
      color: var(--color-toggle-chip-hover-text, #ffffff) !important;
    }

    > .show-on-hover {
      visibility: visible;
      opacity: 1;
      margin-left: 6px;
      transition: opacity 0.3s ease-in-out 0.1s, visibility 0s;
    }
  }
`;

export const EntityButton = styled.button<{
  comingSoon?: boolean;
  $padding?: string;
}>`
  width: 147px;
  height: 147px;
  border-radius: 10px;
  border: 2px solid var(--color-logo-tile-border, var(--color-border, #dbeae3));
  background: var(--color-logo-tile-bg, var(--color-surface-raised, #ffffff));
  color: var(--color-on-logo-tile, var(--color-text-dark, #042a2a));
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ comingSoon }) => (comingSoon ? 'default' : 'pointer')};
  opacity: ${({ comingSoon }) => (comingSoon ? 0.5 : 1)};
  padding: ${({ $padding }) => $padding || '0'};
  margin: 0;
  appearance: none;
  font: inherit;
  &:hover,
  &:focus,
  &:active {
    border: ${({ comingSoon }) =>
      comingSoon
        ? '2px solid var(--color-logo-tile-border, var(--color-border, #dbeae3))'
        : '2px solid var(--color-primary, #41aaaf)'};
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const RoundAddButton = styled(Button).attrs((props) => ({
  className: ['scope-round-add-button', 'scope-icon-chip', props.className]
    .filter(Boolean)
    .join(' '),
  rootClassName: [
    'scope-round-add-button',
    'scope-icon-chip',
    props.rootClassName,
  ]
    .filter(Boolean)
    .join(' '),
}))`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  width: 25px;
  height: 30px;
  gap: 0px;
  border-radius: 50%;
  opacity: 0px;
  angle: -180 deg;
  background: #e9f6f7 !important;
  border: 1px solid #dbeae3 !important;
  color: #042a2a !important;
  box-shadow: none !important;

  &.ant-btn-primary:not(:hover) {
    background: #e9f6f7 !important;
    color: #042a2a !important;
    border-color: #dbeae3 !important;
  }

  .anticon {
    color: #042a2a !important;
  }

  ${(props) =>
    !props.disabled &&
    `
  &:hover {
    background: #e9f6f7 !important;
    border: 1px solid var(--color-primary, #41aaaf) !important;
    color: #042a2a !important;
  }
  `}
`;

const StyledExportButton = styled(Button)
  .withConfig({
    shouldForwardProp: (prop) => !['isGuide'].includes(prop),
  })
  .attrs<{ isGuide?: boolean; className?: string }>((props) => ({
    className:
      [props.className, props.isGuide ? 'scope-export-guide-btn' : '']
        .filter(Boolean)
        .join(' ') || undefined,
  }))`
  ${buttonLabelTruncationStyles}

  border: 1px solid #dbeae3 !important;
  background-color: #ffffff !important;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  box-shadow: none !important;
  text-decoration: none !important;
  color: ${(props) =>
    props.isGuide ? 'var(--color-text-muted, #5a6a6a)' : '#042a2a'} !important;

  > span {
    font-size: 12px;
    font-weight: 600;
    color: ${(props) =>
      props.isGuide ? 'var(--color-text-muted, #5a6a6a)' : '#042a2a'};
    text-decoration: none !important;
  }

  .anticon {
    color: ${(props) =>
      props.isGuide
        ? 'var(--color-text-muted, #5a6a6a)'
        : '#042a2a'} !important;
  }

  ${(props) =>
    !props.disabled &&
    `
  &:not(:disabled):not(.ant-btn-disabled):hover,
  &:not(:disabled):not(.ant-btn-disabled):focus,
  &:not(:disabled):not(.ant-btn-disabled):active {
    border: 1px solid var(--color-primary, #41aaaf) !important;
    background-color: #ffffff !important;
    color: ${
      props.isGuide ? 'var(--color-text-muted, #5a6a6a)' : '#042a2a'
    } !important;
    text-decoration: none !important;
    box-shadow: none !important;

    > span {
      color: ${
        props.isGuide ? 'var(--color-text-muted, #5a6a6a)' : '#042a2a'
      } !important;
      text-decoration: none !important;
    }

    .anticon {
      color: ${
        props.isGuide ? 'var(--color-text-muted, #5a6a6a)' : '#042a2a'
      } !important;
    }
  }
  `}
`;

export const ExportButton = withTruncatedButtonLabel(StyledExportButton);

const StyledContinueButton = styled(Button)`
  ${buttonLabelTruncationStyles}

  font-family: Inter;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  color: var(--color-primary, #41aaaf) !important;
  background-color: var(--color-sso-button-bg, #d2edef) !important;
  border-color: var(--color-sso-button-bg, #d2edef) !important;
  border-radius: 50px;
  width: ${(props) => (props.width ? props.width : '330px')};
  height: ${(props) => (props.height ? props.height : '45px')};
  box-shadow: none;

  &:hover,
  &:focus {
    color: var(--color-primary, #41aaaf) !important;
    background-color: #c4e5e8 !important;
    border-color: #41aaaf !important;
    box-shadow: none;
  }
}`;

export const ContinueButton = withTruncatedButtonLabel(StyledContinueButton);

export const YellowButton = styled(ScopeButton)<{ $textColor?: string }>`
  color: ${(props) =>
    props.$textColor ? props.$textColor : '#FFFFFF'} !important;
  border-color: ${(props) =>
    props.border_color ? props.border_color : '#ffbc3e'} !important;
  font-weight: 700;
  line-height: 20px;
`;

export const SelectButton = styled(ScopeButton).withConfig({
  shouldForwardProp: (prop) =>
    !['color', 'border_color', 'selected', 'bg_color'].includes(prop),
})<{ selected?: boolean }>`
  color: ${(props) => (props.color ? props.color : '#FFFFFF')} !important;
  border: 2px solid
    ${(props) => (props.border_color ? props.border_color : '#FFFFFF')} !important;
  font-weight: 600;
  line-height: 20px;

  &:hover {
    border-color: var(--color-primary, #41aaaf) !important;
    background-color: #ffffff !important;
    color: #042a2a !important;
  }

  ${(props) =>
    props.selected &&
    `
    border-color: var(--color-primary, #41aaaf) !important;
    background-color: var(--color-primary, #41aaaf) !important;
    color: #FFFFFF !important;
  
  &:hover {
    border-color: var(--color-primary, #41aaaf) !important;
    background-color: var(--color-primary, #41aaaf) !important;
    color: #FFFFFF !important;
  }
  `}
`;

export const GrayButton = styled(ScopeButton)`
  color: ${(props) => (props.color ? props.color : '#FFFFFF')} !important;
  border-color: #5a6a6a !important;
  background-color: #5a6a6a !important;
  font-size: 13px;
  font-weight: 700;
  line-height: 24px;
  cursor: default !important;

  &:hover {
    border-color: #5a6a6a !important;
    background-color: #5a6a6a !important;
    color: ${(props) => (props.color ? props.color : '#FFFFFF')} !important;
  }
`;
